import { useCallback, useEffect, useState } from "react";
import { CTeDto, CTeFiltrarDto, STORAGECTE_KEY, getLocal, updateLocal } from "./CTe.Dto";
import useFilterManager from "src/hook/useFilterManager";
import { MapearErrosServidor, parseDate } from "src/hook/Functions";
import {
  CTeBuscarXML,
  CTeConsultarSituacao,
  CTeEnviar,
  CTeExcluir,
  CTeFiltrar,
  CTeImprimir,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { useNavigate } from "react-router-dom";
import CTeMenuCode from "./CTe.Menu.Code";
import moment from "moment";

const getDataInicialPadrao = () => moment().startOf("month").toDate();
const getDataFinalPadrao = () => moment().toDate();

const parseFilterDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const m = moment(dateString);
  return m.isValid() ? m.toDate() : null;
};

const areDatesSameDay = (d1?: Date | null, d2?: Date | null) => {
  if (!d1 && !d2) return true;
  if (!d1 || !d2) return false;
  return moment(d1).isSame(d2, "day");
};

const CTeCode = () => {
  const [data, setData] = useState<CTeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalShowPropriedades, setModalShowPropriedades] = useState(false);
  const [modalNovoShow, setModalNovoShow] = useState(false);
  const [cte, setCTe] = useState<CTeDto>();
  const navigate = useNavigate();

  const handleNovoCTe = useCallback(() => {
    const cteSalvo = getLocal<CTeDto>(STORAGECTE_KEY);
    if (cteSalvo && Object.keys(cteSalvo).length > 0) {
      setModalNovoShow(true);
    } else {
      navigate("/fiscal/cte/cadastro");
    }
  }, [navigate]);

  const menu = CTeMenuCode({
    setLoading,
    data,
    onNovoCTe: handleNovoCTe,
  });

  const useFilter = useFilterManager<CTeFiltrarDto>({
    codigoStatusEnvio: null,
    dataEmissaoInicial: getDataInicialPadrao(),
    dataEmissaoFinal: getDataFinalPadrao(),
    serie: null,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      setLoading(true);
      try {
        await filtrar(useFilter.filters);
      } finally {
        setLoading(false);
      }
    };

    const hasValidDateRange =
      (useFilter.filters.dataEmissaoInicial &&
        useFilter.filters.dataEmissaoFinal) ||
      (!useFilter.filters.dataEmissaoInicial &&
        !useFilter.filters.dataEmissaoFinal);

    if (hasValidDateRange) {
      loadData();
    }

    return () => controller.abort();
  }, [useFilter.filters]);

  useEffect(() => {
    const serieParam = useFilter.searchParams.get("serie");
    const dataInicialParam = useFilter.searchParams.get("dataEmissaoInicial");
    const dataFinalParam = useFilter.searchParams.get("dataEmissaoFinal");
    const semDataParam = useFilter.searchParams.get("semData");

    const isInitialEmpty =
      !dataInicialParam && !dataFinalParam && semDataParam !== "true";

    const dataEmissaoInicial =
      semDataParam === "true"
        ? null
        : isInitialEmpty
        ? getDataInicialPadrao()
        : parseFilterDate(dataInicialParam);

    const dataEmissaoFinal =
      semDataParam === "true"
        ? null
        : isInitialEmpty
        ? getDataFinalPadrao()
        : parseFilterDate(dataFinalParam);

    const newFilters: CTeFiltrarDto = {
      codigoStatusEnvio:
        useFilter.searchParams.get("codigoStatusEnvio") || null,
      dataEmissaoInicial,
      dataEmissaoFinal,
      serie:
        serieParam && Number(serieParam) > 0 ? Number(serieParam) : null,
    };

    useFilter.setFilters((prev) => {
      if (
        prev.codigoStatusEnvio === newFilters.codigoStatusEnvio &&
        prev.serie === newFilters.serie &&
        areDatesSameDay(prev.dataEmissaoInicial, newFilters.dataEmissaoInicial) &&
        areDatesSameDay(prev.dataEmissaoFinal, newFilters.dataEmissaoFinal)
      ) {
        return prev;
      }
      return newFilters;
    });
  }, [useFilter.searchParams, useFilter.setFilters]);

  const filtrar = async (input: CTeFiltrarDto) => {
    try {
      const response = await CTeFiltrar(input);

      if (response.data.success) {
        setData(response.data.data);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao filtrar CT-e:", error);
      showToast({
        message: "Ocorreu erro ao buscar dados",
        title: "Erro",
        variant: "Danger",
      });
    }
  };

  const handleSelectStatus = (eventKey?: string | null) => {
    useFilter.updateFilter(
      "codigoStatusEnvio",
      eventKey === "99" ? null : eventKey
    );
  };

  const handleChangeSerie = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, "");
    useFilter.updateFilter(
      "serie",
      rawValue !== "" && Number(rawValue) > 0 ? Number(rawValue) : null
    );
  };

  const handleSetDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (!start && !end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: null,
        dataEmissaoFinal: null,
      }));
      useFilter.setMultipleFilters({
        dataEmissaoInicial: null,
        dataEmissaoFinal: null,
        semData: "true",
      } as any);
    } else if (start && !end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: start,
        dataEmissaoFinal: null,
      }));
    } else if (start && end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: start,
        dataEmissaoFinal: end,
      }));
      useFilter.setMultipleFilters({
        dataEmissaoInicial: moment(start).format("YYYY-MM-DD"),
        dataEmissaoFinal: moment(end).format("YYYY-MM-DD"),
        semData: null,
      } as any);
    }
  };

  const formatDate = (date: any) =>
    date ? new Date(date).toLocaleDateString("pt-BR") : "Selecionar";

  const getDatePickerTitle = () => {
    const { dataEmissaoInicial, dataEmissaoFinal } = useFilter.filters;

    if (!dataEmissaoInicial || !dataEmissaoFinal) {
      if (dataEmissaoInicial && !dataEmissaoFinal) {
        return `${formatDate(dataEmissaoInicial)} - Selecionar`;
      }
      return "Emissão";
    }

    return `${formatDate(dataEmissaoInicial)} - ${formatDate(
      dataEmissaoFinal
    )}`;
  };

  const onReturnModalPropriedades = () => {
    setModalShowPropriedades(false);
  };

  const onDeleteModalPropriedades = async (value: CTeDto) => {
    if (!value.id) return;
    setLoading(true);
    await CTeExcluir(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao excluir CT-e",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onFetchStatusModalPropriedades = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();
    setLoading(true);
    await CTeConsultarSituacao(value.id)
      .then((response) => {
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao consultar situação",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onSendModalPropriedades = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();
    setLoading(true);
    await CTeEnviar(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
          updateLocal<CTeDto>(STORAGECTE_KEY, null);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao enviar CT-e",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onDownloadXml = async (value: CTeDto) => {
    if (!value.id) return;
    setLoading(true);
    setModalShowPropriedades(false);

    try {
      const nomeArquivo = `CTe_${value.numeroCte}`;
      const response = await CTeBuscarXML([value.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast({
        message: "Ocorreu erro ao baixar XML",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const onPrintModalPropriedadesImprimir = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await CTeImprimir(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const isMobile = /Android|iPhone/i.test(navigator.userAgent);

          if (isMobile) {
            window.open(pdfUrl, "_blank");
          } else {
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "0";
            iframe.style.height = "0";
            iframe.src = pdfUrl;

            document.body.appendChild(iframe);

            iframe.onload = () => {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
            };
          }
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .catch((error) => {
        console.error("Erro ao imprimir DACTE:", error);
        showToast({
          message: "Ocorreu erro ao imprimir DACTE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onModalPropriedadesDownloadDacte = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await CTeImprimir(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = `DacteCTe${value.numeroCte}.pdf`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(pdfUrl);
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .catch((error) => {
        console.error("Erro ao baixar DACTE:", error);
        showToast({
          message: "Ocorreu erro ao baixar DACTE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onModalPropriedadesShareDacte = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await CTeImprimir(value.id)
      .then(async (response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          try {
            const file = new File([pdfBlob], `DacteCTe${value.numeroCte}.pdf`, {
              type: "application/pdf",
            });

            if (navigator.canShare?.({ files: [file] })) {
              await navigator.share({
                title: "DACTE CT-e",
                files: [file],
              });
            } else {
              onModalPropriedadesDownloadDacte(value);
            }
          } catch (err) {
            console.error("Erro ao compartilhar:", err);
          }
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .catch((error) => {
        console.error("Erro ao compartilhar DACTE:", error);
        showToast({
          message: "Ocorreu erro ao compartilhar DACTE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleClickPropriedades = (value: CTeDto) => () => {
    setModalShowPropriedades(true);
    setCTe(value);
  };

  const onEditModalPropriedades = (value: CTeDto) => {
    setModalShowPropriedades(false);
    updateLocal<CTeDto>(STORAGECTE_KEY, value);
    navigate("/fiscal/cte/cadastro");
  };

  const onDuplicateModalPropriedades = (value: CTeDto) => {
    setModalShowPropriedades(false);
    const duplicated: CTeDto = {
      ...value,
      id: 0,
      numeroCte: 0,
    };
    updateLocal<CTeDto>(STORAGECTE_KEY, duplicated);
    navigate("/fiscal/cte/cadastro");
  };

  const onEventModalPropriedades = (value: CTeDto) => {
    setModalShowPropriedades(false);
    navigate(`/fiscal/cte/evento?cteId=${value.id}`);
  };

  const onVisualizarModalPropriedades = (value: CTeDto) => {
    if (!value.id) return;
    window.open(`/fiscal/cte/${value.id}/visualizar`, "_blank");
  };

  const onContinuarModalNovo = () => {
    setModalNovoShow(false);
    navigate("/fiscal/cte/cadastro");
  };

  const onCriarNovoModalNovo = () => {
    setModalNovoShow(false);
    updateLocal<CTeDto>(STORAGECTE_KEY, null);
    navigate("/fiscal/cte/cadastro");
  };

  const onCancelModalNovo = () => {
    setModalNovoShow(false);
  };

  return {
    data,
    loading,
    handleChangeSerie,
    handleSelectStatus,
    handleSetDates,
    useFilter,
    getDatePickerTitle,
    modalShowPropriedades,
    cte,
    onReturnModalPropriedades,
    onDeleteModalPropriedades,
    onFetchStatusModalPropriedades,
    onDownloadXml,
    onPrintModalPropriedadesImprimir,
    onModalPropriedadesDownloadDacte,
    onModalPropriedadesShareDacte,
    handleClickPropriedades,
    onSendModalPropriedades,
    onEditModalPropriedades,
    onDuplicateModalPropriedades,
    onEventModalPropriedades,
    onVisualizarModalPropriedades,
    modalNovoShow,
    onContinuarModalNovo,
    onCriarNovoModalNovo,
    onCancelModalNovo,
    menu,
  };
};

export default CTeCode;
